import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FileText, 
  Download,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Database,
  Zap,
  Shield,
  Clock,
  DollarSign,
  Target,
  BarChart3,
  Settings
} from "lucide-react";

interface Recommendation {
  id: string;
  category: 'Performance' | 'Security' | 'Maintenance' | 'Cost';
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  title: string;
  description: string;
  impact: {
    performance: number;
    cost: number;
    risk: number;
  };
  effort: 'Low' | 'Medium' | 'High';
  timeframe: string;
  sqlCode?: string;
  estimatedSavings?: string;
  dependencies?: string[];
  kpi: {
    before: number;
    after: number;
    unit: string;
    metric: string;
  };
}

export function RecommendationReport() {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  
  const recommendations: Recommendation[] = [
    {
      id: "idx-001",
      category: "Performance",
      priority: "Critical",
      title: "Create Covering Index on Orders Table",
      description: "Add covering index to eliminate key lookups for the most frequent query pattern. This query accounts for 35% of total CPU usage.",
      impact: { performance: 85, cost: 10, risk: 5 },
      effort: "Low",
      timeframe: "1-2 hours",
      sqlCode: `CREATE NONCLUSTERED INDEX IX_Orders_CustomerDate_Covering
ON dbo.Orders (customer_id, order_date)
INCLUDE (total_amount, status, shipping_address)
WITH (ONLINE = ON, FILLFACTOR = 90);`,
      estimatedSavings: "$2,400/month in reduced CPU costs",
      dependencies: [],
      kpi: {
        before: 1250,
        after: 185,
        unit: "ms",
        metric: "Average Query Duration"
      }
    },
    {
      id: "stat-001",
      category: "Performance",
      priority: "High",
      title: "Update Statistics on High-Activity Tables",
      description: "Statistics on Users and Orders tables are severely outdated, causing poor query plan choices and performance degradation.",
      impact: { performance: 70, cost: 5, risk: 10 },
      effort: "Low",
      timeframe: "30 minutes",
      sqlCode: `UPDATE STATISTICS dbo.Users WITH FULLSCAN;
UPDATE STATISTICS dbo.Orders WITH FULLSCAN;
UPDATE STATISTICS dbo.OrderItems WITH FULLSCAN;`,
      estimatedSavings: "$1,200/month in improved performance",
      dependencies: [],
      kpi: {
        before: 45,
        after: 82,
        unit: "%",
        metric: "Query Plan Efficiency"
      }
    },
    {
      id: "part-001",
      category: "Performance",
      priority: "High",
      title: "Implement Table Partitioning for AuditLog",
      description: "AuditLog table has grown to 15GB and is causing performance issues. Partition by date for better query pruning and maintenance.",
      impact: { performance: 90, cost: 30, risk: 25 },
      effort: "High",
      timeframe: "2-3 weeks",
      sqlCode: `-- Create partition function and scheme
CREATE PARTITION FUNCTION PF_AuditLog_Date (datetime2)
AS RANGE RIGHT FOR VALUES 
('2024-01-01', '2024-04-01', '2024-07-01', '2024-10-01');

CREATE PARTITION SCHEME PS_AuditLog_Date
AS PARTITION PF_AuditLog_Date
TO ([PRIMARY], [PRIMARY], [PRIMARY], [PRIMARY], [PRIMARY]);`,
      estimatedSavings: "$3,600/month in improved query performance",
      dependencies: ["Maintenance window required", "Application testing needed"],
      kpi: {
        before: 8500,
        after: 850,
        unit: "ms",
        metric: "Large Query Duration"
      }
    },
    {
      id: "sec-001",
      category: "Security",
      priority: "Critical",
      title: "Enable Transparent Data Encryption (TDE)",
      description: "Database contains sensitive customer information and should be encrypted at rest to meet compliance requirements.",
      impact: { performance: -5, cost: 15, risk: -90 },
      effort: "Medium",
      timeframe: "4-6 hours",
      sqlCode: `-- Create database master key
CREATE MASTER KEY ENCRYPTION BY PASSWORD = 'StrongPassword123!';

-- Create certificate
CREATE CERTIFICATE TDE_Cert WITH SUBJECT = 'TDE Certificate';

-- Create database encryption key
USE [YourDatabase];
CREATE DATABASE ENCRYPTION KEY
WITH ALGORITHM = AES_256
ENCRYPTION BY SERVER CERTIFICATE TDE_Cert;

-- Enable TDE
ALTER DATABASE [YourDatabase] SET ENCRYPTION ON;`,
      estimatedSavings: "Compliance risk reduction: $50,000 potential fine avoidance",
      dependencies: ["Backup certificate to secure location", "Test restore procedures"],
      kpi: {
        before: 0,
        after: 100,
        unit: "%",
        metric: "Data Encryption Coverage"
      }
    },
    {
      id: "maint-001",
      category: "Maintenance",
      priority: "High",
      title: "Implement Automated Index Maintenance",
      description: "Index fragmentation is causing performance degradation. Implement automated maintenance to rebuild/reorganize indexes based on fragmentation levels.",
      impact: { performance: 60, cost: 20, risk: 15 },
      effort: "Medium",
      timeframe: "1 week",
      sqlCode: `-- Create maintenance plan for index optimization
DECLARE @sql NVARCHAR(MAX) = '';
SELECT @sql = @sql + 
    CASE 
        WHEN avg_fragmentation_in_percent > 30 
        THEN 'ALTER INDEX ' + i.name + ' ON ' + s.name + '.' + o.name + ' REBUILD WITH (ONLINE = ON);' + CHAR(13)
        WHEN avg_fragmentation_in_percent > 10 
        THEN 'ALTER INDEX ' + i.name + ' ON ' + s.name + '.' + o.name + ' REORGANIZE;' + CHAR(13)
        ELSE ''
    END
FROM sys.dm_db_index_physical_stats(DB_ID(), NULL, NULL, NULL, 'LIMITED') ps
JOIN sys.indexes i ON ps.object_id = i.object_id AND ps.index_id = i.index_id
JOIN sys.objects o ON i.object_id = o.object_id
JOIN sys.schemas s ON o.schema_id = s.schema_id
WHERE avg_fragmentation_in_percent > 10;`,
      estimatedSavings: "$1,800/month in maintenance time reduction",
      dependencies: ["Schedule during maintenance window", "Monitor space requirements"],
      kpi: {
        before: 25,
        after: 5,
        unit: "%",
        metric: "Average Index Fragmentation"
      }
    },
    {
      id: "cost-001",
      category: "Cost",
      priority: "Medium",
      title: "Optimize Query Store Configuration",
      description: "Query Store is consuming excessive space and causing overhead. Optimize retention and capture settings.",
      impact: { performance: 25, cost: 40, risk: 5 },
      effort: "Low",
      timeframe: "1 hour",
      sqlCode: `ALTER DATABASE [YourDatabase] SET QUERY_STORE = ON
(
    OPERATION_MODE = READ_WRITE,
    CLEANUP_POLICY = (STALE_QUERY_THRESHOLD_DAYS = 30),
    DATA_FLUSH_INTERVAL_SECONDS = 900,
    MAX_STORAGE_SIZE_MB = 1024,
    INTERVAL_LENGTH_MINUTES = 60,
    SIZE_BASED_CLEANUP_MODE = AUTO,
    QUERY_CAPTURE_MODE = AUTO
);`,
      estimatedSavings: "$600/month in storage and overhead reduction",
      dependencies: [],
      kpi: {
        before: 4096,
        after: 1024,
        unit: "MB",
        metric: "Query Store Size"
      }
    }
  ];

  const filteredRecommendations = selectedCategory === "all" 
    ? recommendations 
    : recommendations.filter(r => r.category === selectedCategory);

  const priorityOrder = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
  const sortedRecommendations = [...filteredRecommendations].sort(
    (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
  );

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-shiftleft-100 text-shiftleft-800 border-shiftleft-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Performance': return <TrendingUp className="w-4 h-4" />;
      case 'Security': return <Shield className="w-4 h-4" />;
      case 'Maintenance': return <Settings className="w-4 h-4" />;
      case 'Cost': return <DollarSign className="w-4 h-4" />;
      default: return <Database className="w-4 h-4" />;
    }
  };

  const totalSavings = recommendations.reduce((sum, rec) => {
    const savings = rec.estimatedSavings?.match(/\$(\d{1,3}(?:,\d{3})*)/);
    return sum + (savings ? parseInt(savings[1].replace(/,/g, '')) : 0);
  }, 0);

  const exportReport = () => {
    const reportData = {
      generatedAt: new Date().toISOString(),
      summary: {
        totalRecommendations: recommendations.length,
        criticalIssues: recommendations.filter(r => r.priority === 'Critical').length,
        estimatedMonthlySavings: totalSavings,
        averagePerformanceImpact: Math.round(recommendations.reduce((sum, r) => sum + r.impact.performance, 0) / recommendations.length)
      },
      recommendations: recommendations
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sql-optimization-report-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                SQL Performance Optimization Report
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Generated on {new Date().toLocaleDateString()} • {recommendations.length} recommendations
              </p>
            </div>
            <Button onClick={exportReport} variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export Report
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="summary" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="summary">Executive Summary</TabsTrigger>
              <TabsTrigger value="recommendations">Detailed Recommendations</TabsTrigger>
              <TabsTrigger value="implementation">Implementation Plan</TabsTrigger>
              <TabsTrigger value="roi">ROI Analysis</TabsTrigger>
            </TabsList>

            <TabsContent value="summary" className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-red-50 border-red-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="font-medium text-red-800">Critical Issues</span>
                  </div>
                  <div className="text-2xl font-bold text-red-600">
                    {recommendations.filter(r => r.priority === 'Critical').length}
                  </div>
                  <div className="text-sm text-red-600">Require immediate attention</div>
                </Card>

                <Card className="p-4 bg-green-50 border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Monthly Savings</span>
                  </div>
                  <div className="text-2xl font-bold text-green-600">
                    ${totalSavings.toLocaleString()}
                  </div>
                  <div className="text-sm text-green-600">Estimated cost reduction</div>
                </Card>

                <Card className="p-4 bg-shiftleft-50 border-shiftleft-200">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-5 h-5 text-shiftleft-600" />
                    <span className="font-medium text-shiftleft-800">Performance Gain</span>
                  </div>
                  <div className="text-2xl font-bold text-shiftleft-600">
                    {Math.round(recommendations.reduce((sum, r) => sum + r.impact.performance, 0) / recommendations.length)}%
                  </div>
                  <div className="text-sm text-shiftleft-600">Average improvement</div>
                </Card>

                <Card className="p-4 bg-purple-50 border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-800">Quick Wins</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-600">
                    {recommendations.filter(r => r.effort === 'Low').length}
                  </div>
                  <div className="text-sm text-purple-600">Low effort, high impact</div>
                </Card>
              </div>

              {/* Category Breakdown */}
              <Card className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Recommendations by Category
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {['Performance', 'Security', 'Maintenance', 'Cost'].map(category => {
                    const categoryRecs = recommendations.filter(r => r.category === category);
                    const criticalCount = categoryRecs.filter(r => r.priority === 'Critical').length;
                    
                    return (
                      <div key={category} className="p-4 border rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          {getCategoryIcon(category)}
                          <span className="font-medium">{category}</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Total:</span>
                            <span className="font-semibold">{categoryRecs.length}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Critical:</span>
                            <span className={`font-semibold ${criticalCount > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                              {criticalCount}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Avg Impact:</span>
                            <span className="font-semibold">
                              {categoryRecs.length > 0 
                                ? Math.round(categoryRecs.reduce((sum, r) => sum + r.impact.performance, 0) / categoryRecs.length)
                                : 0}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Top Recommendations Preview */}
              <Card className="p-6">
                <h3 className="font-semibold mb-4">Top Priority Recommendations</h3>
                <div className="space-y-3">
                  {sortedRecommendations.slice(0, 3).map((rec) => (
                    <div key={rec.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge className={getPriorityColor(rec.priority)}>
                          {rec.priority}
                        </Badge>
                        <div>
                          <div className="font-medium">{rec.title}</div>
                          <div className="text-sm text-gray-600">{rec.kpi.metric}: {rec.kpi.before} → {rec.kpi.after} {rec.kpi.unit}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-green-600">+{rec.impact.performance}% performance</div>
                        <div className="text-xs text-gray-500">{rec.timeframe}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="recommendations" className="space-y-4">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-medium">Filter by category:</span>
                <div className="flex gap-2">
                  <Button
                    variant={selectedCategory === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory("all")}
                  >
                    All ({recommendations.length})
                  </Button>
                  {['Performance', 'Security', 'Maintenance', 'Cost'].map(category => (
                    <Button
                      key={category}
                      variant={selectedCategory === category ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedCategory(category)}
                    >
                      {category} ({recommendations.filter(r => r.category === category).length})
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                {sortedRecommendations.map((rec) => (
                  <Card key={rec.id} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {getCategoryIcon(rec.category)}
                        <div>
                          <h4 className="font-semibold text-lg">{rec.title}</h4>
                          <p className="text-gray-600 mt-1">{rec.description}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={getPriorityColor(rec.priority)}>
                          {rec.priority} Priority
                        </Badge>
                        <Badge variant="outline">{rec.effort} Effort</Badge>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="p-3 bg-shiftleft-50 border border-shiftleft-200 rounded">
                        <div className="text-sm text-shiftleft-600 mb-1">Performance Impact</div>
                        <div className="text-xl font-bold text-shiftleft-600">+{rec.impact.performance}%</div>
                      </div>
                      <div className="p-3 bg-green-50 border border-green-200 rounded">
                        <div className="text-sm text-green-600 mb-1">KPI Improvement</div>
                        <div className="text-sm font-semibold text-green-600">
                          {rec.kpi.metric}: {rec.kpi.before} → {rec.kpi.after} {rec.kpi.unit}
                        </div>
                      </div>
                      <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                        <div className="text-sm text-purple-600 mb-1">Implementation Time</div>
                        <div className="text-xl font-bold text-purple-600">{rec.timeframe}</div>
                      </div>
                    </div>

                    {rec.sqlCode && (
                      <div className="mb-4">
                        <div className="text-sm font-medium mb-2">Implementation SQL:</div>
                        <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-x-auto">
                          {rec.sqlCode}
                        </pre>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t">
                      <div className="text-sm text-gray-600">
                        Estimated Savings: <span className="font-semibold text-green-600">{rec.estimatedSavings}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          <Clock className="w-4 h-4 mr-1" />
                          Schedule
                        </Button>
                        <Button size="sm">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Implement
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="implementation" className="space-y-4">
              <Card className="p-6">
                <h3 className="font-semibold mb-4">Recommended Implementation Sequence</h3>
                <div className="space-y-4">
                  {sortedRecommendations.map((rec, index) => (
                    <div key={rec.id} className="flex items-center gap-4 p-4 border rounded-lg">
                      <div className="flex-shrink-0 w-8 h-8 bg-shiftleft-600 text-white rounded-full flex items-center justify-center font-semibold">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{rec.title}</div>
                        <div className="text-sm text-gray-600">{rec.timeframe} • {rec.effort} effort</div>
                        {rec.dependencies && rec.dependencies.length > 0 && (
                          <div className="text-xs text-yellow-600 mt-1">
                            Dependencies: {rec.dependencies.join(", ")}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <Badge className={getPriorityColor(rec.priority)}>
                          {rec.priority}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="roi" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Cost-Benefit Analysis
                  </h3>
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded">
                      <div className="text-lg font-bold text-green-600">${totalSavings.toLocaleString()}/month</div>
                      <div className="text-sm text-green-600">Total estimated savings</div>
                    </div>
                    <div className="p-4 bg-shiftleft-50 border border-shiftleft-200 rounded">
                      <div className="text-lg font-bold text-shiftleft-600">${(totalSavings * 12).toLocaleString()}/year</div>
                      <div className="text-sm text-shiftleft-600">Annual savings projection</div>
                    </div>
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded">
                      <div className="text-lg font-bold text-purple-600">2-4 weeks</div>
                      <div className="text-sm text-purple-600">Estimated payback period</div>
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Performance Improvements
                  </h3>
                  <div className="space-y-4">
                    {['Query Response Time', 'CPU Utilization', 'Index Efficiency'].map((metric, index) => {
                      const improvements = [65, 40, 85];
                      return (
                        <div key={metric} className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>{metric}</span>
                            <span className="font-semibold text-green-600">+{improvements[index]}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full" 
                              style={{ width: `${improvements[index]}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}